"use client"

import { TrendingUp } from "lucide-react"

import { GlassCard } from "@shared/ui/landing/glass-card"
import { SectionContainer } from "@shared/ui/landing/section-container"
import { StatCounter } from "@shared/ui/landing/stat-counter"
import { StatusTag } from "@shared/ui/landing/status-tag"

import { ApprovalProtectionStatus } from "@/components/approval-protection-status"
import { SecurityScoreChart } from "@/components/visuals/security-score-chart"
import { useTranslations } from "@/lib/i18n/client"

import {
  scoreBreakdown,
  SECURITY_SCORE,
  SECURITY_SCORE_MAX,
} from "@/lib/live-data"

export function SecurityScoreSection() {
  const t = useTranslations("score")

  return (
    <SectionContainer id="score" className="py-12 md:py-20">
      <ApprovalProtectionStatus />

      <div className="mb-8 space-y-2 text-center md:mb-12">
        <StatusTag variant="success">{t("badge")}</StatusTag>
        <h2 className="text-2xl font-bold tracking-tight md:text-3xl">{t("title")}</h2>
        <p className="mx-auto max-w-2xl text-muted-foreground">{t("subtitle")}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-5">
        <GlassCard className="cyber-border relative overflow-hidden lg:col-span-2">
          <div className="flex flex-col items-center justify-center py-6">
            <div className="relative">
              <svg className="size-40 -rotate-90 md:size-48" viewBox="0 0 120 120">
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="rgba(255,255,255,0.08)"
                  strokeWidth="8"
                />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="url(#scoreGradient)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(SECURITY_SCORE / SECURITY_SCORE_MAX) * 327} 327`}
                />
                <defs>
                  <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="100%" stopColor="#34d399" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <StatCounter
                  value={SECURITY_SCORE}
                  className="text-4xl font-bold md:text-5xl"
                />
                <span className="text-sm text-muted-foreground">
                  / {SECURITY_SCORE_MAX}
                </span>
              </div>
            </div>
            <p className="mt-4 flex items-center gap-1.5 text-sm text-emerald-400">
              <TrendingUp className="size-4" />
              {t("trendLabel")}
            </p>
          </div>
        </GlassCard>

        <GlassCard className="cyber-border lg:col-span-3">
          <h3 className="mb-4 text-sm font-medium text-muted-foreground">
            {t("trendTitle")}
          </h3>
          <SecurityScoreChart />
        </GlassCard>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {scoreBreakdown.map((item) => (
          <GlassCard key={item.id} className="cyber-border p-4">
            <p className="text-xs text-muted-foreground">{t(item.labelKey)}</p>
            <div className="mt-2 flex items-end justify-between">
              <StatCounter value={item.score} className="text-2xl font-bold" />
              <span className="text-xs text-emerald-400">/100</span>
            </div>
            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full rounded-full bg-gradient-to-r from-sky-500 to-emerald-400"
                style={{ width: `${item.score}%` }}
              />
            </div>
          </GlassCard>
        ))}
      </div>
    </SectionContainer>
  )
}
